import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import StyledDialog from "../StyledDialog";
import ThumbnailField from "../ThumbnailField";
import PdfUploadField from "../PdfUploadField";
import { usePdfUpload } from "../../hooks/usePdfUpload";
import { buildUpdateLectureFileName, examTypeNameForLecture } from "../../lib/pdfFileName";
import { updateOnlineLecture, updateGeneralLecture } from "../../../../api/onlineLecture";

// Lecture update modal. Preserved legacy behaviors:
//  - pdfUrl seeded with the existing LectureNoteUrl so renamefile has a source
//  - renamefile fired (NOT awaited) alongside the update when a pdfUrl exists
//  - endpoint routed by SubCourseTopicUniqueId == 0 (general vs normal)
//  - null values omitted from the FormData (no new thumbnail → key not sent)
//  - the YouTube URL input's edit is NOT merged into the payload (3-year live
//    quirk, kept intentionally — see docs/OnlineLecturePanel.md Appendix B)
//  - on API failure the modal stays open so work isn't lost
const UpdateLectureModal = ({ open, onClose, lecture, segments, adminToken, onUpdated }) => {
    const pdf = usePdfUpload(adminToken);
    const isGeneral = lecture.SubCourseTopicUniqueId == 0;

    const defaultSubTopicName =
        lecture.SubTopicName && lecture.SubTopicName.includes("/")
            ? lecture.SubTopicName.split("/")[1].trim()
            : isGeneral
            ? lecture.SubTopicName
            : "";

    const [title, setTitle] = useState(lecture.Title || "");
    const [topicName, setTopicName] = useState(lecture.TopicName || "");
    const [subTopicName, setSubTopicName] = useState(defaultSubTopicName || "");
    const [streamUrl, setStreamUrl] = useState(lecture.StreamUrl || "");
    const [offlineUrl, setOfflineUrl] = useState(lecture.OfflineUrl || "");
    const [isPublic, setIsPublic] = useState(Boolean(lecture.IsPublic));
    const [thumbnailFile, setThumbnailFile] = useState(null);
    const [thumbnailPreview, setThumbnailPreview] = useState(null);

    useEffect(() => {
        pdf.seed(lecture.LectureNoteUrl);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const pdfFileName = () =>
        buildUpdateLectureFileName({
            examTypeName: examTypeNameForLecture(segments, lecture),
            lecture,
            titleInput: title,
            topicInput: topicName,
            subTopicInput: subTopicName,
        });

    // Name-field edits invalidate an uploaded PDF (silently in the update flow)
    const nameFieldChange = (setter) => (e) => {
        setter(e.target.value);
        pdf.invalidate(false);
    };

    const handleSubmit = async (event) => {
        event.preventDefault();

        // Fire-and-forget rename (runs in parallel with the update, like legacy)
        if (pdf.pdfUrl) {
            pdf.rename(pdfFileName(), lecture.Id, onUpdated);
        }

        const updatedLecture = {
            ...lecture,
            SubTopicName: subTopicName,
            StreamUrl: streamUrl,
            ThumbnailUrl: thumbnailFile,
            LectureNoteUrl: pdf.pdfUrl,
            IsPublic: isPublic,
        };
        if (isGeneral) {
            updatedLecture.Title = title;
            updatedLecture.TopicName = topicName;
        }

        const formData = new FormData();
        formData.append("OnlineLectureTeacherId", updatedLecture.TeacherInfos.TeacherId);
        for (const key in updatedLecture) {
            if (key !== "Homeworks" && key !== "TeacherInfos" && updatedLecture[key] != null) {
                formData.append(key, updatedLecture[key]);
            }
        }

        const res = await (isGeneral
            ? updateGeneralLecture(formData, adminToken)
            : updateOnlineLecture(formData, adminToken));
        if (res.status == 200) {
            if (res.data.ResponseStatus == 1) {
                toast.success("Ders Güncelleme başarılı");
                onUpdated();
                onClose();
            } else {
                toast.error(res.data.ResponseMessage);
            }
        }
    };

    return (
        <StyledDialog open={open} onClose={onClose}>
            <div className="olp-theme olp-modal">
                <h2 className="olp-modal-title">Dersi Güncelle</h2>
                <div className="olp-modal-readonly">
                    {lecture.CourseName && (
                        <div>
                            Ders: <b>{lecture.CourseName}</b>
                        </div>
                    )}
                    <div>
                        Tarih: <b>{lecture.Date}</b>
                    </div>
                    {lecture.TopicName && (
                        <div>
                            Konu: <b>{lecture.TopicName}</b>
                        </div>
                    )}
                    {lecture.SubTopicName && (
                        <div>
                            Alt Başlık: <b>{lecture.SubTopicName}</b>
                        </div>
                    )}
                </div>
                <form onSubmit={handleSubmit}>
                    {isGeneral && (
                        <React.Fragment>
                            <div className="olp-field">
                                <label htmlFor="Title">Ana Başlık Giriniz: </label>
                                <input
                                    className="olp-input"
                                    required
                                    id="Title"
                                    name="Title"
                                    type="text"
                                    value={title}
                                    onChange={nameFieldChange(setTitle)}
                                />
                            </div>
                            <div className="olp-field">
                                <label htmlFor="TopicName">Başlık Giriniz: </label>
                                <input
                                    className="olp-input"
                                    required
                                    id="TopicName"
                                    name="TopicName"
                                    type="text"
                                    value={topicName}
                                    onChange={nameFieldChange(setTopicName)}
                                />
                            </div>
                        </React.Fragment>
                    )}
                    <div className="olp-field">
                        <label htmlFor="SubTopicName">Alt Başlık Giriniz(part1,part2): </label>
                        <input
                            className="olp-input"
                            id="SubTopicName"
                            name="SubTopicName"
                            type="text"
                            value={subTopicName}
                            onChange={nameFieldChange(setSubTopicName)}
                        />
                    </div>
                    <div className="olp-field olp-field--check">
                        <input
                            type="checkbox"
                            checked={isPublic}
                            name="PublicStream"
                            id="PublicStream"
                            onChange={() => setIsPublic(!isPublic)}
                        />
                        <label htmlFor="PublicStream">Herkese açık yayın</label>
                    </div>
                    <div className="olp-field">
                        <label htmlFor="UrlInput">Castr Url Giriniz: </label>
                        <input
                            className="olp-input"
                            name="StreamUrl"
                            type="text"
                            id="UrlInput"
                            value={streamUrl}
                            onChange={(e) => setStreamUrl(e.target.value)}
                        />
                    </div>
                    <div className="olp-field">
                        <label htmlFor="OfflineUrlInput">YouTube Url Giriniz: </label>
                        <input
                            className="olp-input"
                            name="OfflineUrl"
                            type="text"
                            id="OfflineUrlInput"
                            value={offlineUrl}
                            onChange={(e) => setOfflineUrl(e.target.value)}
                        />
                    </div>
                    <ThumbnailField
                        currentUrl={lecture.ThumbnailUrl}
                        previewUrl={thumbnailPreview}
                        onSelect={(file, objectUrl) => {
                            setThumbnailFile(file);
                            setThumbnailPreview(objectUrl);
                        }}
                    />
                    {lecture.LectureNoteUrl && (
                        <div className="olp-field">
                            <label>Mevcut Ders Notu: </label>
                            <a className="olp-link" href={lecture.LectureNoteUrl} target="_blank" rel="noopener noreferrer">
                                {lecture.LectureNoteUrl}
                            </a>
                        </div>
                    )}
                    <PdfUploadField pdf={pdf} onFileSelected={(file) => pdf.upload(file, pdfFileName())} />
                    <div className="olp-modal-footer">
                        <button type="button" className="olp-btn" onClick={onClose}>
                            Vazgeç
                        </button>
                        <button type="submit" className="olp-btn olp-btn--primary" disabled={pdf.submitLocked}>
                            Güncelle
                        </button>
                    </div>
                </form>
            </div>
        </StyledDialog>
    );
};

export default UpdateLectureModal;
